import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  orderBy,
  query,
  type Unsubscribe,
  type DocumentData,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db, storage } from "../firebase/config";
import { getTodayId } from "../utils/date";
import type { JournalEntry, NewEntry } from "./types";

// ── Firestore → domain mapper ───────────────────────────────────────────────

function toJournalEntry(id: string, data: DocumentData): JournalEntry {
  return {
    id,
    title: (data.title as string) ?? "",
    content: (data.content as string) ?? "",
    hasVoiceNote: (data.hasVoiceNote as boolean) ?? false,
    voiceNoteUrl: (data.voiceNoteUrl as string) ?? "",
    voiceTranscript: (data.voiceTranscript as string) ?? "",
    date: data.createdAt?.toDate?.() ?? new Date(),
    dateId: (data.dateId as string) ?? "",
    weekNumber: (data.weekNumber as number) ?? 1,
    createdAt: data.createdAt?.toDate?.(),
    updatedAt: data.updatedAt?.toDate?.(),
  };
}

// ── Real-time listener ──────────────────────────────────────────────────────

export function subscribeToEntries(
  uid: string,
  onData: (entries: JournalEntry[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const ref = collection(db, "users", uid, "journalEntries");
  const q = query(ref, orderBy("createdAt", "desc"));

  return onSnapshot(
    q,
    (snapshot) => {
      const entries = snapshot.docs.map((d) => toJournalEntry(d.id, d.data()));
      onData(entries);
    },
    (err) => onError(err),
  );
}

// ── CRUD ────────────────────────────────────────────────────────────────────

export async function createEntry(
  uid: string,
  entry: Omit<NewEntry, "dateId" | "weekNumber" | "voiceNoteUrl"> & { voiceNoteUrl?: string },
  currentWeek: number,
  audioBlob?: Blob | null,
): Promise<void> {
  let voiceNoteUrl = entry.voiceNoteUrl ?? "";
  if (audioBlob) {
    voiceNoteUrl = await uploadVoiceNote(uid, audioBlob);
  }

  const ref = collection(db, "users", uid, "journalEntries");
  await addDoc(ref, {
    ...entry,
    voiceNoteUrl,
    dateId: getTodayId(),
    weekNumber: currentWeek,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function patchEntry(
  uid: string,
  entryId: string,
  fields: Partial<NewEntry>,
  audioBlob?: Blob | null,
): Promise<void> {
  let voiceNoteUrl = fields.voiceNoteUrl;
  if (audioBlob) {
    voiceNoteUrl = await uploadVoiceNote(uid, audioBlob);
  }

  const ref = doc(db, "users", uid, "journalEntries", entryId);
  await updateDoc(ref, {
    ...fields,
    ...(voiceNoteUrl !== undefined && { voiceNoteUrl }),
    updatedAt: serverTimestamp(),
  });
}

export async function removeEntry(
  uid: string,
  entryId: string,
  voiceNoteUrl?: string,
): Promise<void> {
  if (voiceNoteUrl) {
    await deleteVoiceNoteByUrl(voiceNoteUrl);
  }
  const ref = doc(db, "users", uid, "journalEntries", entryId);
  await deleteDoc(ref);
}

// ── Storage helpers 

async function uploadVoiceNote(uid: string, blob: Blob): Promise<string> {
  const filename = `voiceNotes/${uid}/${Date.now()}.webm`;
  const fileRef = storageRef(storage, filename);
  await uploadBytes(fileRef, blob, { contentType: blob.type || "audio/webm" });
  return getDownloadURL(fileRef);
}

async function deleteVoiceNoteByUrl(url: string): Promise<void> {
  try {
    const pathMatch = url.match(/\/o\/(.+?)\?/);
    if (pathMatch) {
      const path = decodeURIComponent(pathMatch[1]);
      const fileRef = storageRef(storage, path);
      await deleteObject(fileRef);
    }
  } catch {
    // Non-fatal — 
  }
}
